import { AxiosError } from "axios";
import moment from "moment";
import HttpService from "./http";

export type PlanChangeTiming = "IMMEDIATE" | "PERIOD_END";

export type PlanChangeStatus =
  | "PENDING_PAYMENT"
  | "APPLIED"
  | "SCHEDULED"
  | "CANCELED"
  | "EXPIRED"
  | "FAILED";

export type PlanChangeResultStatus =
  | "APPLIED"
  | "REQUIRES_ACTION"
  | "SCHEDULED";

/** PayMod's dates are UTC without zone (`2027-01-12T10:00:00`), or a day without time: read them with `parsePayModDate`. */
export type PayModDate = string;

/** Amounts are integers in the smallest unit of `currency` (cents), tax included where named so. */
export interface PlanChangeLine {
  description: string;
  amount: number;
  proration?: boolean;
}

export interface PlanChangeOverLimit {
  key: string;
  limit: number;
  usage: number;
}

/**
 * PayMod's preview, with SUM's reading: `change`, `timing`, and for a downgrade the limits already exceeded
 * (`over_limits`) and those whose usage could not be read (`unchecked_limits`). Empty lists for an upgrade.
 * `PERIOD_END` has no invoice: amounts 0, `effective_at` = end of the period.
 */
export interface PlanChangePreview {
  from_plan_code: string;
  to_plan_code: string;
  change: "upgrade" | "downgrade";
  timing: PlanChangeTiming;
  amount_due: number;
  subtotal: number;
  tax: number;
  total?: number;
  currency: string;
  proration_date: number;
  effective_at: PayModDate;
  current_period_end: PayModDate;
  next_amount: number;
  lines?: PlanChangeLine[];
  over_limits: PlanChangeOverLimit[];
  unchecked_limits: string[];
}

export interface PlanChange {
  plan_change_identification: string;
  from_plan_code: string;
  to_plan_code: string;
  timing: PlanChangeTiming;
  change?: "upgrade" | "downgrade";
  status: PlanChangeStatus;
  effective_at: PayModDate | null;
  invoice_id?: string | null;
  amount_due?: number | null;
  amount_paid?: number | null;
  currency?: string | null;
  created_at?: PayModDate | null;
}

export interface PlanChangeResult {
  status: PlanChangeResultStatus;
  hosted_invoice_url?: string | null;
  payment_error?: string | null;
  effective_at?: PayModDate | null;
  plan_change?: PlanChange | null;
}

export interface BillingAddress {
  line1: string;
  line2?: string;
  postal_code?: string;
  city: string;
  state?: string;
  country: string;
}

/**
 * Why the back end refused, read from `element.reason` of a 400: PayMod's `error_code`, relayed as is, or one of
 * SUM's own refusals named the same way (PREVIEW_REQUIRED is SUM's alone).
 */
export const PLAN_CHANGE_REFUSALS = [
  "INVALID_REQUEST",
  "PLAN_NOT_CHANGEABLE",
  "PLAN_EXCLUDED",
  "SAME_PLAN",
  "SERVICE_MISMATCH",
  "CURRENCY_MISMATCH",
  "NO_ACTIVE_SUBSCRIPTION",
  "TARGET_PLAN_ALREADY_ACTIVE",
  "PAID_BY_PAYER",
  "PAYMENT_LINK",
  "SUBSCRIPTION_NOT_ACTIVE",
  "CANCELLATION_PENDING",
  "PRICE_MISMATCH",
  "CHANGE_IN_PROGRESS",
  "SCHEDULE_ATTACHED",
  "MONTHLY_ONLY_AT_PERIOD_END",
  "CREDIT_NOT_ALLOWED",
  "PRORATION_DATE_EXPIRED",
  "BILLING_ADDRESS_REQUIRED",
  "PAYMENT_DECLINED",
  "STRIPE_REFUSED",
  "PLAN_CHANGE_NOT_FOUND",
  "NOT_CANCELABLE",
  "ALREADY_APPLIED",
  "NOT_CANCELED",
  "SUBSCRIPTION_ENDED",
  "INVALID_ADDRESS",
  "PREVIEW_REQUIRED",
] as const;

export type PlanChangeRefusal = (typeof PLAN_CHANGE_REFUSALS)[number];

const billing = (customerId: string, entityId: string) =>
  `/${customerId}/${entityId}/billing`;

export const PLAN_CHANGE_ROUTES = {
  preview: (c: string, e: string) => `${billing(c, e)}/plan-change/preview`,
  apply: (c: string, e: string) => `${billing(c, e)}/plan-change`,
  list: (c: string, e: string) => `${billing(c, e)}/plan-change`,
  cancel: (c: string, e: string, id: string) =>
    `${billing(c, e)}/plan-change/${encodeURIComponent(id)}`,
  billingAddress: (c: string, e: string) =>
    `${billing(c, e)}/plan-change/billing-address`,
  cancelAtPeriodEnd: (c: string, e: string, plan: string) =>
    `${billing(c, e)}/${encodeURIComponent(plan)}/cancel-at-period-end`,
  resume: (c: string, e: string, plan: string) =>
    `${billing(c, e)}/${encodeURIComponent(plan)}/resume-subscription`,
};

export const planChangeRefusal = (
  err: unknown
): PlanChangeRefusal | undefined => {
  const reason = (err as AxiosError<any>)?.response?.data?.element?.reason;
  return PLAN_CHANGE_REFUSALS.includes(reason) ? reason : undefined;
};

const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A day alone is that day everywhere: through UTC, it would read the day before west of UTC. */
export const parsePayModDate = (value: PayModDate | null | undefined) => {
  if (!value) return moment.invalid();
  return DAY_ONLY.test(value)
    ? moment(value, "YYYY-MM-DD")
    : moment.utc(value).local();
};

export const formatPayModDate = (
  value: PayModDate | null | undefined,
  format: string,
  absent = ""
) => {
  const parsed = parsePayModDate(value);
  return parsed.isValid() ? parsed.format(format) : absent;
};

export const planChangeErrorMessage = (err: unknown): string | undefined => {
  const axiosError = err as AxiosError<any>;
  return (
    axiosError?.response?.data?.element?.details ||
    axiosError?.response?.data?.message ||
    undefined
  );
};

export const newRequestId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const PlanChangeService = {
  preview: async (
    customerId: string,
    entityId: string,
    body: { from_plan: string; to_plan: string }
  ) =>
    (
      await HttpService.post(
        PLAN_CHANGE_ROUTES.preview(customerId, entityId),
        body
      )
    ).data.element as PlanChangePreview,

  apply: async (
    customerId: string,
    entityId: string,
    body: {
      from_plan: string;
      to_plan: string;
      proration_date: number | string;
      request_id: string;
    }
  ) =>
    (
      await HttpService.post(
        PLAN_CHANGE_ROUTES.apply(customerId, entityId),
        body
      )
    ).data.element as PlanChangeResult,

  list: async (customerId: string, entityId: string): Promise<PlanChange[]> => {
    const element = (
      await HttpService.get(PLAN_CHANGE_ROUTES.list(customerId, entityId))
    ).data.element;
    return Array.isArray(element) ? element : element?.changes ?? [];
  },

  cancel: async (customerId: string, entityId: string, id: string) =>
    (
      await HttpService.delete(
        PLAN_CHANGE_ROUTES.cancel(customerId, entityId, id)
      )
    ).data.element as PlanChange,

  /** Asked when a preview or a change answers BILLING_ADDRESS_REQUIRED; preview again afterwards. */
  saveBillingAddress: async (
    customerId: string,
    entityId: string,
    body: { from_plan: string; billing_address: BillingAddress }
  ) =>
    (
      await HttpService.put(
        PLAN_CHANGE_ROUTES.billingAddress(customerId, entityId),
        body
      )
    ).data.element,

  resumeSubscription: async (
    customerId: string,
    entityId: string,
    plan: string
  ) =>
    (
      await HttpService.post(
        PLAN_CHANGE_ROUTES.resume(customerId, entityId, plan),
        null
      )
    ).data.element,
};

export default PlanChangeService;
